<template>
  <div
    class="artwork-image"
    :class="{
      'artwork-image--with-made': withMade,
      'artwork-image__t-shirt': showImageProof && !showImagePreview,
      'artwork-image__t-shirt--gang-sheet-upload':
        showImageProof && !showImagePreview && showGangSheetBuilderFeature,
      'artwork-image__t-shirt--gang-sheet-upload-with-canva-enabled': gangSheetAndCanvaEnabled
    }"
  >
    <div
      v-if="showImagePreview"
      class="artwork-image__preview"
      :class="{
        'artwork-image__preview--empty': !imageSrc,
        'artwork-image__preview--with-error': withError,
        'artwork-image__preview--without-width': !artwork.width,
        'artwork-image__preview--full-height': showFullWidthArtwork,
        'artwork-image__preview--fit-to-container': fitToContainer
      }"
    >
      <div
        v-if="!withMade || (withMade && imageSrc)"
        class="artwork-image__preview-wrapper"
        :class="{
          'artwork-image__preview-wrapper--without-height': !artwork.height,
          'artwork-image__preview-wrapper--max-width': showFullWidthArtwork && artwork.height
        }"
      >
        <div
          class="artwork-image__preview-image"
          :class="{
            'artwork-image__preview-image--loaded': fillAvailable,
            'artwork-image__preview-image--transparent-background': isTransparent,
            'artwork-image__preview-image--full-width': showFullWidthArtwork,
            'artwork-image__preview-image--standard-artwork': isStandardArtwork
          }"
        >
          <div v-if="artwork.processingImage" class="artwork-image__loader">
            <Spinner theme="white" class="artwork-image__loader-spinner" />
          </div>
          <template v-if="isStandardArtwork">
            <div
              v-if="isArtworkLoading && !spangleSizeEditing"
              class="artwork-image__loading-wrapper"
            >
              <div class="artwork-image__loading-box-content">
                <UseSvg
                  class="artwork-image__loading-box-content-icon mx-auto"
                  icon="cloud-upload-2"
                />
                <DtfProgressBar v-if="isArtworkLoading" :artwork="artwork" :index="index" />
              </div>
            </div>
          </template>
          <template v-else>
            <div
              v-if="isArtworkLoading && !spangleSizeEditing"
              class="artwork-image__sparkle-gif-container"
            >
              <img :src="sparkleGif" alt="Jiffy Designs" class="artwork-image__sparkle-gif" />
            </div>
          </template>

          <div
            v-if="showSpanglesLoader"
            class="artwork-image__preview-overlay-loader"
            :class="{ 'artwork-image__preview-overlay-loader--editing-size': spangleSizeEditing }"
          >
            <UseSvg
              v-if="spangleSizeEditing"
              class="artwork-image__preview-overlay-loader-icon"
              icon="pencil"
            />
            <span
              v-if="spangleSizeEditing"
              class="artwork-image__loader-text"
              v-html="spanglesEditingText"
            />
            <ProcessingProgress
              v-else
              :message="spanglesLoaderText"
              class="artwork-image__loader-text"
            />
            <button
              v-if="!spangleSizeEditing"
              type="button"
              class="artwork-image__cancel"
              @click="handleCancelProcess(artwork)"
            >
              Cancel
            </button>
          </div>

          <div
            v-if="!showImageProof && !isStandardArtwork && isArtworkLoading"
            class="artwork-image__preview-overlay-loader artwork-image__preview-overlay-loader--tips-wrapper"
            :class="{ 'artwork-image__preview-overlay-loader--tips-wrapper-modal': showModal }"
          >
            <div
              class="artwork-image__preview-overlay-loader-jiffy-tips"
              :class="{ 'artwork-image__preview-overlay-loader-jiffy-tips--modal': showModal }"
            >
              <UseSvg
                class="artwork-image__preview-overlay-loader-tips-icon"
                icon="lightbulb-info"
              />
              <span>JIFFY TIPS</span>
            </div>

            <span
              class="artwork-image__loader-text"
              :class="{ 'artwork-image__loader-text--modal': showModal }"
              v-html="currentMessage"
            >
            </span>
          </div>

          <div
            v-if="artwork.width"
            class="artwork-image__arrow-shaft artwork-image__arrow-shaft--horizontal"
            :class="{
              'artwork-image__arrow-shaft--horizontal-compact': showFullWidthArtwork,
              'artwork-image__arrow-shaft--standard-artwork': isStandardArtwork
            }"
          >
            <div class="artwork-image__dimension">{{ arrowWidth }}”</div>
            <div class="artwork-image__arrow artwork-image__arrow--left" />
            <div class="artwork-image__arrow artwork-image__arrow--right" />
          </div>
          <div
            v-if="artwork.height"
            class="artwork-image__arrow-shaft artwork-image__arrow-shaft--vertical"
            :class="{
              'artwork-image__arrow-shaft--vertical-compact': showFullWidthArtwork,
              'artwork-image__arrow-shaft--standard-artwork': isStandardArtwork
            }"
          >
            <div
              class="artwork-image__dimension"
              :class="{ 'artwork-image__dimension--vertical': showFullWidthArtwork }"
            >
              {{ arrowHeight }}{{ heightUnit }}
            </div>
            <div class="artwork-image__arrow artwork-image__arrow--top" />
            <div class="artwork-image__arrow artwork-image__arrow--bottom" />
          </div>
          <div
            ref="imageWrapper"
            class="artwork-image__image-wrapper image-magnifier"
            :class="[
              getImageZoomId,
              {
                'artwork-image__image-wrapper--loading': isArtworkImageWrapperLoading
              }
            ]"
            @click="annotateImage"
            @touchstart="annotateImage"
          >
            <div v-if="isLoading" class="artwork-image__loader">
              <Spinner theme="white" class="artwork-image__loader-indicator" />
            </div>
            <div
              v-if="showMagnifier"
              class="image-magnifier__preview js-magnify-preview"
              :data-background-color="artworkBackground"
            ></div>
            <div class="image-magnifier__lens js-magnify-lens"></div>
            <ImageCompare
              ref="artwork"
              :before-image="sliderBeforeImage"
              :after-image="shouldDisableSlider ? sliderBeforeImage : imageSrc"
              :magnifier-position="magnifierPosition"
              :before-text="sliderText.before"
              :after-text="sliderText.after"
              :show-magnifier="!isTappable && showMagnifier"
              :slider-position="sliderPosition"
              :image-background="artworkBackground"
              :is-full-width="showFullWidthArtwork"
              :class="{
                'artwork-image__image js-magnify-image': true,
                'artwork-image__image--full-width': showFullWidthArtwork,
                'artwork-image__image--fit-to-container': fitToContainer,
                'artwork-image__image--standard-artwork': isStandardArtwork
              }"
              :enhancement-processing="artworkEnhancementProcessing"
              is-artwork
              alt="Preview Image"
              :selected-label="activeConfigSection === 'jiffy-print-perfect' ? 'after' : 'before'"
              :enhancement-tier="artwork.enhancementTier"
              @load="onImageLoad"
              @canvas-loaded="onCanvasLoaded"
            >
              <template
                v-if="isStandardArtwork && !sliderText.before && !shouldDisableSlider"
                #beforeText
              >
                Before
                <span v-if="shouldShowLabels" class="artwork-image__comparison-slider-label">
                  ({{ Math.round(artwork.sizeBasedDpi) }} DPI)
                </span>
              </template>
              <template
                v-if="isStandardArtwork && !sliderText.after && !shouldDisableSlider"
                #afterText
              >
                {{ imageCompareAfterLabel }}
                <span v-if="shouldShowLabels" class="artwork-image__comparison-slider-label">
                  ({{ artwork.enhancedDpi }} DPI)
                </span>
              </template>
            </ImageCompare>
            <div
              v-for="(point, i) in annotatedPoints"
              :key="i"
              :class="`artwork-image__${point.mode}`"
              :style="{
                left: (point.x + annotationXOffset - 5).toString() + 'px',
                top: (point.y + annotationYOffset - 5).toString() + 'px'
              }"
            />
          </div>
          <button
            v-if="showMagnifier && !annotateEnabled"
            class="artwork-image__zoom-button"
            :class="{ 'artwork-image__zoom-button--t-shirt': showImageProof }"
            type="button"
            @click="toggleComparisonModal"
          >
            <UseSvg icon="search" class="artwork-image__zoom-icon" />
          </button>
        </div>
        <span v-if="showMagnifier" :class="`artwork-image__zoom-text hidden ${getZoomTextId}`">
          {{ zoomText }}
        </span>
      </div>
      <Spinner v-else class="artwork-image__spinner" :paused="pauseSpinner" />
      <CommonModal v-if="showComparisonModal" @on-close="toggleComparisonModal">
        <ImageCompare
          :before-image="sliderBeforeImage"
          :after-image="shouldDisableSlider ? sliderBeforeImage : imageSrc"
          :before-text="sliderText.before"
          :after-text="sliderText.after"
          :show-magnifier="isTappable"
          :is-inline="isTappable"
          :slider-position="sliderPosition"
          :image-background="artworkBackground"
          :enhancement-processing="artworkEnhancementProcessing"
          :selected-label="activeConfigSection === 'jiffy-print-perfect' ? 'after' : 'before'"
          :enhancement-tier="artwork.enhancementTier"
          @canvas-loaded="onCanvasLoaded"
        >
          <template
            v-if="isStandardArtwork && !sliderText.before && !shouldDisableSlider"
            #beforeText
          >
            Before
            <span class="artwork-image__comparison-slider-label">
              ({{ Math.round(artwork.sizeBasedDpi) }} DPI)
            </span>
          </template>
          <template
            v-if="isStandardArtwork && !sliderText.after && !shouldDisableSlider"
            #afterText
          >
            {{ imageCompareAfterLabel }}
            <span class="artwork-image__comparison-slider-label">
              ({{ artwork.enhancedDpi }} DPI)
            </span>
          </template>
        </ImageCompare>
      </CommonModal>
    </div>
    <div v-else :class="uploadAreaStyles" @click="onDropzoneClick">
      <div
        v-if="showUploadCloudArea"
        class="artwork-image__cloud"
        :class="{ 'artwork-image__cloud--canva-enabled': uploadFromCanvaEnabled }"
      >
        <UseSvg icon="upload-cloud-box" class="artwork-image__cloud-box" />
        <UseSvg icon="upload-cloud-border" class="artwork-image__cloud-border" />
      </div>
      <div v-if="showGangSheetBuilderFeature" class="artwork-image__gang-sheet-note">
        <UseSvg icon="lightbulb-info" class="artwork-image__gang-sheet-note-icon" />
        <span>
          Gang sheet width should be <b>22” (6600px)</b> with <b>300 DPI</b>.
          <a :href="dpiGuidelines" @click.stop> View Image Guidelines </a>
        </span>
      </div>
      <div class="artwork-image__info hidden-xs">
        <SpangleNotice v-if="isSpangle && !showImageProof" />
        <PuffNotice v-if="isPuff && !showImageProof" />
        <UseSvg
          v-else-if="!showUploadCloudArea"
          icon="cloud-upload"
          class="artwork-image__icon"
          :class="{ 'artwork-image__icon--standard-artwork': isStandardArtwork }"
        />
        <p class="artwork-image__title">{{ uploadAreaTitle }}</p>
        <p v-if="showDropzoneSubtitle" class="artwork-image__subtitle">
          Multiple designs? Drag or select all your files together.
        </p>
        <p
          class="artwork-image__divider"
          :class="{ 'artwork-image__divider--standard-artwork': isStandardArtwork }"
        ></p>
      </div>
      <div
        v-if="isSpangle && !showImageProof"
        class="artwork-image__spangle-mobile-notice visible-xs"
      >
        <SpangleNotice />
      </div>
      <div v-if="isPuff && !showImageProof" class="artwork-image__spangle-mobile-notice visible-xs">
        <PuffNotice />
      </div>
      <div class="artwork-image__action-buttons">
        <GangSheetBuilderContainer v-if="showGangSheetBuilderFeature" />
        <div
          class="artwork-image__action"
          :class="{ 'artwork-image__action--standard-artwork': isStandardArtwork }"
        >
          <template v-if="showUploadCloudArea || showImageProof">
            <span class="artwork-image__action-title">{{ uploadButtonTitle }}</span>
            <span class="artwork-image__action-title--mobile">{{ uploadButtonTitle }}</span>
          </template>
          <template v-else>
            <span class="hidden-xs">{{ uploadButtonTitle }}</span>
            <span class="artwork-image__action-container">
              <UseSvg
                icon="cloud-upload"
                class="artwork-image__icon"
                :class="{ 'artwork-image__icon--standard-artwork': isStandardArtwork }"
              />
              {{ uploadButtonTitle }}
            </span>
          </template>
        </div>
      </div>
      <a
        v-if="isUserSignedIn"
        href="javascript:;"
        class="artwork-image__dropzone-button artwork-image__dropzone-button--purchase"
        :class="{
          'artwork-image__dropzone-button--standard-artwork': isStandardArtwork
        }"
        @click="onArtworkHistoryClick"
        @click.stop
      >
        {{ uploadAreaPreviousAction }}
      </a>
      <span v-else class="artwork-image__sign-in">
        <a
          class="artwork-image__link"
          :class="{
            'artwork-image__link--standard-artwork': isStandardArtwork
          }"
          href="/login"
          @click.stop
        >
          Log in
        </a>
        to see purchased designs.
      </span>
      <div
        v-if="uploadFromCanvaEnabled"
        class="artwork-image__dropzone-button artwork-image__dropzone-button--purchase artwork-image__dropzone-button--canva"
        :class="{
          'artwork-image__dropzone-button--standard-artwork': isStandardArtwork
        }"
        @click.stop
      >
        <CanvaButton :file-landing="'#product-details-dtf-file-input'" :namespace="'dtf'" />
      </div>
    </div>
    <DtfProgressBar
      v-if="!isStandardArtwork && isArtworkLoading"
      :artwork="artwork"
      :index="index"
    />
    <ArtworkFullScreen
      v-if="showImagePreview && showFullScreenButton"
      :with-made="withMade"
      :artwork="artwork"
      :active-config-section="activeConfigSection"
    />
    <button
      v-if="showCancelButton"
      type="button"
      :class="[
        'artwork-image__cancel',
        'artwork-image__cancel--dtf',
        isStandardArtwork ? 'artwork-image__cancel--dtf-v2' : ''
      ]"
      @click="handleCancelProcess(artwork)"
    >
      <UseSvg class="artwork-image__cancel-icon" icon="modal-close" />
      Cancel
    </button>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import ImageMagnifier from "components/jiffy/image-magnifier/image-magnifier";
import { isTouchDevice, isMobile } from "common/utils/browser";
import CanvaButton from "components/jiffy/canva-button/CanvaButton.vue";
import CommonModal from "common/vue/components/CommonModal.vue";
import Spinner from "../vue-cart/Spinner.vue";
import SpangleNotice from "./SpangleNotice.vue";
import PuffNotice from "./PuffNotice.vue";
import ImageCompare from "./common/ImageCompare.vue";
import ProcessingProgress from "./ProcessingProgress.vue";
import DtfProgressBar from "./DtfProgressBar.vue";
import ArtworkFullScreen from "./ArtworkFullScreen.vue";
import GangSheetBuilderContainer from "./GangSheetBuilderContainer.vue";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";
import { UPLOAD_BUTTON_LABEL, ARTWORK_IMAGE_MESSAGES } from "./common/constants";

export default {
  name: "ArtworkImage",
  components: {
    CanvaButton,
    Spinner,
    UseSvg,
    ImageCompare,
    CommonModal,
    SpangleNotice,
    PuffNotice,
    ProcessingProgress,
    DtfProgressBar,
    GangSheetBuilderContainer,
    ArtworkFullScreen
  },
  props: {
    artwork: {
      type: Object,
      default: null
    },
    withMade: {
      type: Boolean,
      default: false
    },
    index: {
      type: Number,
      default: 0
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    },
    fitToContainer: {
      type: Boolean,
      default: false
    },
    activeConfigSection: {
      type: String,
      default: null
    },
    showFullScreenButton: {
      type: Boolean,
      default: false
    }
  },
  emits: ["click"],
  data() {
    return {
      imageLoaded: false,
      inchesInAFoot: 12.0,
      wrapperMaxHeight: 348.0, // Image wrapper max height
      /*
        canvasWidthOffSet is the offset needed when testing cropping of image
        in UI to be comparable with imagemagick cropping
      */
      canvasWidthOffSet: 3,
      imageMagnifier: null,
      showComparisonModal: false,
      sliderPosition: 50,
      sparkleGif: "https://jiffy.imgix.net/gen/spangle-sparkle.gif?ixlib=rb-0.3.5&auto=format",
      spanglesLoaderText: "Making spangle magic! Your design is being created.",
      spanglesEditingText: "Please finish editing the size.",
      messages: ARTWORK_IMAGE_MESSAGES,
      currentIndex: 0,
      annotationXOffset: 0,
      annotationYOffset: 0,
      downscaledOriginalImage: null,
      dpiGuidelines: "https://www.jiffy.com/artwork-dpi-instructions"
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "pdpType",
      "printAsIs",
      "userType",
      "locale",
      "showModal",
      "sizeErrorTypes",
      "catalogNumber",
      "showArtworkHistoryModal",
      "businessLine",
      "isSpangle",
      "isProColor",
      "isPuff",
      "isSticker",
      "isStickerSample",
      "pickFromUploadsOrDesigns",
      "puff",
      "artworkBackground",
      "removeBgLoading",
      "isMobile",
      "sticker",
      "experiments",
      "canvaDtfEnabled",
      "isStandardArtwork",
      "enableAbAiMonetization",
      "showGangSheetBuilder",
      "gangSheetBuilder",
      "showGangSheetBuilderFeature",
      "previewCanvasSize"
    ]),
    ...mapState(useSpanglesStore, [
      "spangleSizeEditing",
      "isSelectedVersionLoading",
      "selectedVersion",
      "originalArtworkUrl",
      "lastSpangledDimensions",
      "spangleVariations",
      "allAPIsCompleted"
    ]),
    isLoading() {
      if (!this.isPuff) {
        return false;
      }

      return this.puff?.isLoading;
    },
    puffImage() {
      if (this.isPuff) {
        return this.puff?.image;
      }
      return null;
    },
    imageSrc() {
      if (!this.artwork) return null;

      const tryToFindImageSource = () => {
        if (this.puffImage) {
          return this.puffImage;
        }
        if (this.artwork.spangled && this.artwork.spangledImageUrl) {
          return this.artwork.spangledImageUrl;
        }
        if (this.artwork.isFile && this.artwork.file) {
          if (this.artwork.file?.type === "application/pdf") {
            return null;
          }
          return URL.createObjectURL(this.artwork.file);
        }

        if (this.isStandardArtwork) {
          if (this.artwork?.enhancementCompleted) {
            if (this.artwork?.basicPipelineData && this.artwork?.enhancementTier === "basic") {
              return this.artwork?.removeBackground
                ? this.artwork?.basicPipelineData?.noBgNoTrimUrl
                : (this.artwork?.basicPipelineData?.withBgUrl ??
                    this.artwork?.basicPipelineData?.url);
            }
            return this.artwork?.removeBackground
              ? this.artwork?.sliderPreviewUrl
              : this.artwork?.enhancedWithBgUrl;
          }

          return this.downscaledOriginalImage || this.artwork?.originalImageUrl;
        }
        return this.artwork?.url || this.artwork?.originalImageUrl;
      };

      return tryToFindImageSource() || this.artwork?.url;
    },
    showImagePreview() {
      const baseCondition = this.withMade
        ? this.artwork
        : this.artwork && !this.artwork.isSimulated;

      if (
        this.sizeErrorTypes.includes(this.artwork.errorType?.toString()) &&
        !this.artwork.uploadError
      )
        return baseCondition;
      if (this.showModal) return baseCondition;
      return baseCondition && !this.withError && !this.artwork.retryFailed;
    },
    fillAvailable() {
      if (this.artwork.isFile) {
        return false;
      }
      return this.imageLoaded;
    },
    isUserSignedIn() {
      return this.userType === "signed_in";
    },
    metadata() {
      const { removeBackground, metadata, withBgMetadata } = this.artwork || {};

      // if the image already comes with transparency
      if (!withBgMetadata) {
        return metadata;
      }

      // otherwise, check for the flag for the switch, metadata will be image with background
      return removeBackground ? metadata : withBgMetadata;
    },
    arrowWidth() {
      if (this.isStandardArtwork) {
        if (this.artwork.enhanceImage && this.artwork.enhancementTier !== "custom") {
          if (this.artwork.removeBackground) {
            return (
              this.artwork.enhancedMetadata?.widthInch ||
              this.metadata?.widthInch ||
              this.artwork.width
            );
          }
          return (
            this.artwork.withBgMetadata?.widthInch || this.metadata?.widthInch || this.artwork.width
          );
        }

        return (
          this.artwork?.originalMetadata?.widthInch ||
          this.metadata?.widthInch ||
          this.artwork.width
        );
      }

      if (this.previewMetadata?.widthInch) return this.previewMetadata.widthInch;
      if (this.isSet) return this.artwork.width;

      return this.metadata?.widthInch;
    },
    arrowHeight() {
      if (this.isStandardArtwork) {
        if (this.artwork.enhanceImage && this.artwork.enhancementTier !== "custom") {
          if (this.artwork.removeBackground) {
            return (
              this.artwork.enhancedMetadata?.heightInch ||
              this.metadata?.heightInch ||
              this.artwork.height
            );
          }
          return (
            this.artwork.withBgMetadata?.heightInch ||
            this.metadata?.heightInch ||
            this.artwork.height
          );
        }

        return (
          this.artwork?.originalMetadata?.heightInch ||
          this.metadata?.heightInch ||
          this.artwork.height
        );
      }

      const previewHeight = this.previewMetadata?.heightInch;

      // Convert inch height to the nearest foot
      // e.g. 11 inches to 1 foot, 29 inches to 3 feet
      if (previewHeight) return Math.ceil(previewHeight / this.inchesInAFoot);
      if (this.isSet) return this.artwork.height;

      return this.metadata.heightInch;
    },
    isSet() {
      return this.artwork?.height > 0 && this.artwork?.width > 0;
    },
    previewMetadata() {
      return this.artwork.previewMetadata;
    },
    isPreviewMatadataPresent() {
      return (
        typeof this.previewMetadata !== "object" || Object.keys(this.previewMetadata).length === 0
      );
    },
    heightUnit() {
      return this.printAsIs ? "′" : "”";
    },
    imageMaxHeight() {
      const { artwork } = this.$refs;
      const computations = window.getComputedStyle(artwork);
      const imageMaxHeight = computations.getPropertyValue("max-height");

      return this.pxToNumber(imageMaxHeight);
    },
    previewWrapperPadding() {
      const { previewWrapper } = this.$refs;
      const computations = window.getComputedStyle(previewWrapper);
      const leftPadding = computations.getPropertyValue("padding-left");

      return this.pxToNumber(leftPadding);
    },
    canvasWidth() {
      if (this.isPreviewMatadataPresent) return null;
      if (!this.$refs.artwork?.nodeType) return null;

      const { previewWrapper } = this.$refs;

      if (this.previewMetadata.gangSheetRatio < 1) {
        return this.imageMaxHeight * this.previewMetadata.gangSheetRatio + this.canvasWidthOffSet;
      }

      // previewWrapperPadding is the left padding from .artwork-image__preview-wrapper.
      // This will make sure that the canvas width is within
      // .artwork-image__preview-wrapper
      return previewWrapper.clientWidth - this.previewWrapperPadding;
    },
    canvasHeight() {
      if (this.isPreviewMatadataPresent) return null;
      if (!this.$refs.artwork?.nodeType) return null;
      if (this.previewMetadata.gangSheetRatio < 1) return this.imageMaxHeight;
      return this.canvasWidth / this.previewMetadata.gangSheetRatio;
    },
    canvasWidthPx() {
      if (!this.canvasWidth) return "auto";

      return `${this.canvasWidth}px`;
    },
    canvasHeightPx() {
      if (this.canvasHeight == null) return null;

      return `${this.canvasHeight}px`;
    },
    imageWidth() {
      if (this.isPreviewMatadataPresent || this.previewMetadata?.cropImage) {
        return "unset";
      }

      return `${this.canvasWidth * this.previewMetadata.widthToGangSheetWidthRatio}px`;
    },
    imagePreviewHeight() {
      if (this.canvasHeight == null) return null;

      return `${this.canvasHeight * this.previewMetadata.heightToGangSheetHeightRatio}px`;
    },
    imageAlignment() {
      if (this.isPreviewMatadataPresent) return "center";
      return "unset";
    },
    imagePreviewMaxWidth() {
      if (this.previewMetadata?.cropImage) return "unset";

      return "100%";
    },
    withError() {
      return (
        this.metadata?.errors?.length > 0 || this.artwork.errorMessage || this.artwork.uploadError
      );
    },
    pauseSpinner() {
      return this.artwork.uploadError && this.artwork.file.type === "application/pdf";
    },
    getUniqueArtworkId() {
      return `${this.index}-${this.artwork.id}-${this.isMultiUpload ? "multi" : "single"}`;
    },
    getImageZoomId() {
      return `js-magnify-wrapper-${this.getUniqueArtworkId}`;
    },
    isTappable() {
      return isTouchDevice() || isMobile();
    },
    zoomText() {
      if (this.isTappable) return "";

      return "Hover Image to Zoom";
    },
    getZoomTextId() {
      return `js-zoom-text-${this.getUniqueArtworkId}`;
    },
    isArtworkLoading() {
      return this.artwork.isLoading;
    },
    isArtworkImageWrapperLoading() {
      return this.isArtworkLoading || !this.artwork.enhancementCompleted;
    },
    imageCompareAfterLabel() {
      let label = "Jiffy Print Perfect";

      if (this.enableAbAiMonetization) {
        if (this.artwork.enhancementTier === "basic") {
          label = "Basic";
        } else {
          label = "PrintPerfect";
        }
      }

      return label;
    },
    showSpanglesLoader() {
      return (
        this.isSpangle &&
        (this.spangleSizeEditing || this.isSelectedVersionLoading || this.isArtworkLoading)
      );
    },
    showSpanglesComparison() {
      return (
        this.selectedVersion &&
        this.selectedVersion.spangleCsv &&
        !this.selectedVersion.isLoading &&
        !this.selectedVersion.error &&
        !this.isArtworkLoading
      );
    },
    showImageComparison() {
      if (this.imageLoaded && this.artwork?.success) {
        if (this.isSpangle) {
          return this.showSpanglesComparison;
        }
        if (!this.isSticker && this.isStandardArtwork) {
          return true;
        }
        return this.isProColor;
      }
      return false;
    },
    cmykImage() {
      return this.imageLoaded && this.artwork?.removeBackground
        ? this.artwork?.cmykPreviewImageNoBackgroundUrl
        : this.artwork?.cmykPreviewImageWithBackgroundUrl;
    },
    magnifierPosition() {
      return this.isMultiUpload ? "right" : "left";
    },
    sliderText() {
      if (this.artwork?.isLoading) return {};

      if (this.isProColor) return { before: "CMYK", after: "ProColor" };
      if (this.isSpangle) return { before: "Original", after: "Spangle" };
      return {};
    },
    sliderBeforeImage() {
      if (this.isProColor && !this.isArtworkLoading) {
        return this.cmykImage || this.downscaledOriginalImage || this.artwork?.originalImageUrl;
      }

      if (this.artwork?.enhancementTier === "custom") {
        if (this.artwork?.originalMime === "image/svg+xml") {
          return this.artwork?.trimmedWithBgUrl || this.artwork?.originalImageUrl;
        }
        return this.artwork?.originalImageUrl;
      }

      if (this.showSpanglesComparison) return this.originalArtworkUrl;
      if (!this.isSticker && this.isStandardArtwork) {
        if (
          this.artwork?.enhancementCompleted &&
          this.isArtworkTrimmedWithBackground &&
          this.artwork?.trimmedWithBgUrl
        ) {
          return this.artwork?.trimmedWithBgUrl;
        }
        return this.downscaledOriginalImage || this.artwork?.originalImageUrl || this.imageSrc;
      }
      return this.imageSrc;
    },
    isArtworkTrimmedWithBackground() {
      return !this.artwork?.canRemoveBg && this.artwork?.metadata?.trimmed;
    },
    isTransparent() {
      return this.artworkBackground === "transparent";
    },
    showMagnifier() {
      if (this.isStandardArtwork) {
        return false;
      }

      if (this.isSpangle) {
        return this.imageLoaded && this.allAPIsCompleted;
      }

      return this.imageLoaded;
    },
    messageArray() {
      return this.isSticker ? this.messages.stickers : this.messages.transfers;
    },
    currentMessage() {
      return this.messageArray[this.currentIndex];
    },
    pdpImageWidth() {
      return this.showImageComparison ? "auto" : "650px";
    },
    showFullWidthArtwork() {
      if (this.fitToContainer) {
        return false;
      }

      if (this.isStandardArtwork) {
        return true;
      }

      return this.isSpangle && !this.sticker && !this.isMultiUpload;
    },
    showUploadCloudArea() {
      return this.sticker || this.catalogNumber === "UVDTF01";
    },
    showImageProof() {
      return !this.sticker && !this.showUploadCloudArea;
    },
    uploadAreaStyles() {
      return {
        "artwork-image__dropzone": true,
        "artwork-image__dropzone--canva-enabled": this.cloudAndCanvaEnabled,
        "artwork-image__dropzone--cloud": this.showUploadCloudArea,
        "artwork-image__dropzone--t-shirt": this.showImageProof,
        "artwork-image__dropzone--gang-sheet": this.showGangSheetBuilderFeature,
        "artwork-image__dropzone--standard-artwork": this.isStandardArtwork
      };
    },
    uploadAreaTitle() {
      if (this.showUploadCloudArea || this.showImageProof) {
        return "Drag your design here";
      }
      return "Get started by dragging your design";
    },
    uploadAreaPreviousAction() {
      if (this.showUploadCloudArea || this.showImageProof) {
        return "My Design Library";
      }

      if (this.pickFromUploadsOrDesigns) {
        return "Pick from previous uploads or saved designs";
      }

      return "Purchase from your previous uploads";
    },
    showDropzoneSubtitle() {
      return (
        !this.isSpangle &&
        !this.isPuff &&
        !this.showUploadCloudArea &&
        !this.isStickerSample &&
        !this.showImageProof
      );
    },
    showImageReady() {
      return (
        (this.isArtworkLoading || this.showFloatingImageReadyBlock) &&
        !this.isSpangle &&
        !this.stickers &&
        this.showImageProof
      );
    },
    showImageReadyBlock() {
      return (this.isArtworkLoading || this.showFloatingImageReadyBlock) && this.showImageProof;
    },
    showFloatingImageReadyBlock() {
      return this.showImageProof && !this.showImagePreview;
    },
    annotateEnabled() {
      return this.artwork.annotateEnabled;
    },
    annotatedPoints() {
      return this.artwork.annotatedPoints;
    },
    artworkEnhancementProcessing() {
      return this.isStandardArtwork && !this.isArtworkLoading && !this.artwork.enhancementCompleted;
    },
    artworkMetadata() {
      return this.artwork.enhancementTier === "basic"
        ? (this.artwork.basicPipelineData?.metadata ?? this.artwork.metadata?.basicPipeline)
        : this.artwork.metadata;
    },
    enableNegativeSpaceTool() {
      return (
        this.experiments.enable_negative_space_creation_tool === 1 &&
        this.artworkMetadata.imageVectorized
      );
    },
    shouldSkipShowArrowShaftCalculation() {
      return (
        !this.imageLoaded ||
        !this.artworkMetadata?.trimCoordinate ||
        !this.$refs.artwork?.$refs?.afterImageElement ||
        this.artwork?.enhancementTier === "print_original" ||
        this.artwork?.enhancementTier === "custom" ||
        this.artworkEnhancementProcessing
      );
    },
    arrowShaftHorizontalLeftPadding() {
      if (
        this.shouldSkipShowArrowShaftCalculation ||
        !this.$refs.artwork?.afterImageIntrinsicWidth
      ) {
        return 0;
      }

      const minLeftOffsetFromAfterImage = Math.min(
        this.artworkMetadata.trimCoordinate.topLeft[0],
        this.artworkMetadata.trimCoordinate.bottomLeft[0]
      );

      const leftPadding =
        (this.$refs.artwork.$refs.afterImageElement.getBoundingClientRect().width *
          minLeftOffsetFromAfterImage) /
        this.$refs.artwork.afterImageIntrinsicWidth;

      return `${leftPadding}px`;
    },
    arrowShaftHorizontalWidth() {
      if (
        this.shouldSkipShowArrowShaftCalculation ||
        !this.$refs.artwork?.afterImageIntrinsicWidth
      ) {
        return `100%`;
      }

      const maxHorizontalOffsetFromAfterImage = Math.max(
        this.artworkMetadata.trimCoordinate.topRight[0],
        this.artworkMetadata.trimCoordinate.bottomRight[0]
      );

      const horizontalWidth =
        (this.$refs.artwork.$refs.afterImageElement.getBoundingClientRect().width *
          maxHorizontalOffsetFromAfterImage) /
        this.$refs.artwork.afterImageIntrinsicWidth;

      return `calc(${horizontalWidth}px - ${this.arrowShaftHorizontalLeftPadding})`;
    },
    arrowShaftVerticalTopPadding() {
      if (
        this.shouldSkipShowArrowShaftCalculation ||
        !this.$refs.artwork?.afterImageIntrinsicHeight
      ) {
        return 0;
      }

      const minTopOffsetFromAfterImage = Math.min(
        Math.abs(this.artworkMetadata.trimCoordinate.topLeft[1]),
        Math.abs(this.artworkMetadata.trimCoordinate.topRight[1])
      );

      const topPadding =
        (this.$refs.artwork.$refs.afterImageElement.getBoundingClientRect().height *
          minTopOffsetFromAfterImage) /
        this.$refs.artwork.afterImageIntrinsicHeight;

      return `${topPadding}px`;
    },
    arrowShaftVerticalHeight() {
      if (
        this.shouldSkipShowArrowShaftCalculation ||
        !this.$refs.artwork?.afterImageIntrinsicHeight
      ) {
        return `100%`;
      }

      const maxVerticalOffsetFromAfterImage = Math.max(
        Math.abs(this.artworkMetadata.trimCoordinate.bottomLeft[1]),
        Math.abs(this.artworkMetadata.trimCoordinate.bottomRight[1])
      );

      const verticalHeight =
        (this.$refs.artwork.$refs.afterImageElement.getBoundingClientRect().height *
          maxVerticalOffsetFromAfterImage) /
        this.$refs.artwork.afterImageIntrinsicHeight;

      return `calc(${verticalHeight}px - ${this.arrowShaftVerticalTopPadding})`;
    },
    uploadButtonTitle() {
      return UPLOAD_BUTTON_LABEL;
    },
    uploadFromCanvaEnabled() {
      return this.canvaDtfEnabled && this.isUserSignedIn;
    },
    gangSheetAndCanvaEnabled() {
      return this.showGangSheetBuilderFeature && this.uploadFromCanvaEnabled;
    },
    cloudAndCanvaEnabled() {
      return this.showUploadCloudArea && this.uploadFromCanvaEnabled;
    },
    shouldDisableSlider() {
      return (
        this.artwork.enhancementTier === "print_original" ||
        this.artwork.enhancementTier === "custom"
      );
    },
    artworkUploaded() {
      const artworkId = parseInt(this.artwork?.id, 10);
      if (Number.isNaN(artworkId)) {
        return false;
      }

      return typeof artworkId === "number" && !this.artwork?.isLoading;
    },
    showCancelButton() {
      const isEnhancing = this.artworkUploaded && this.artworkEnhancementProcessing;
      return !this.withError && this.isMultiUpload && (this.isArtworkLoading || isEnhancing);
    },
    shouldShowLabels() {
      if (this.catalogNumber === "DTF001") {
        return !this.experiments.ab_transfers_preview_blur;
      }

      return !this.isMultiUpload && !this.withMade;
    }
  },
  watch: {
    artwork() {
      this.imageLoaded = !this.artwork.isFile;
      this.downscaledOriginalImage = this.artwork.downscaledOriginalImage;
    },
    previewCanvasSize(newVal) {
      if (
        !this.isArtworkLoading &&
        this.artwork.enhancementCompleted &&
        newVal.width > 0 &&
        newVal.height > 0
      ) {
        this.updatePreviewDpi({ artworkId: this.artwork.id });
        this.downscaleOriginalImage({ artworkId: this.artwork.id });
      }
    }
  },
  mounted() {
    this.sliderPosition = this.isSpangle ? 0 : 50;
    setInterval(this.randomMessage, 10000);
  },
  methods: {
    ...mapActions(useDtfStore, [
      "setShowArtworkHistoryModal",
      "removeArtworkByIndex",
      "removeArtwork",
      "updateArtworkValuesById",
      "updateArtworkValues"
    ]),
    ...mapActions(useDtfStore, [
      "updatePreviewDpi",
      "downscaleOriginalImage",
      "handleCancelProcess"
    ]),
    onCanvasLoaded() {
      this.downscaleOriginalImage({ artworkId: this.artwork.id });
    },
    trackAddFileAreaClick() {
      submitDtfEvent("transfer_choose_file_clicked", {
        page: "pdp",
        pdp_type: this.pdpType,
        catalog_number: this.catalogNumber,
        business_line: this.businessLine
      });
    },
    onDropzoneClick() {
      this.trackAddFileAreaClick();
      this.$emit("click", { replaceArtwork: true });
    },
    initializeImageZoom() {
      if (this.showImageComparison) return;
      if (!this.isTappable) {
        this.imageMagnifier = new ImageMagnifier(this.getImageZoomId, {
          isModal: this.isMultiUpload,
          position: this.isMultiUpload ? "right" : "left"
        });
        this.imageMagnifier.initializeMagnifier();
      }
    },
    onImageLoad() {
      this.imageLoaded = !this.artwork.isFile;

      if (!this.imageLoaded) return;
      if (!this.isSpangle) {
        this.initializeImageZoom();
      }
      document.querySelector(`.${this.getZoomTextId}`)?.classList?.remove("hidden");
    },
    pxToNumber(pixelString) {
      return Number(pixelString.replace("px", ""));
    },
    onArtworkHistoryClick() {
      this.setShowArtworkHistoryModal(!this.showArtworkHistoryModal);
      submitDtfEvent("previous_purchase_link_clicked", {
        page: "pdp",
        pdp_type: this.pdpType
      });
    },
    toggleComparisonModal() {
      this.showComparisonModal = !this.showComparisonModal;
    },
    randomMessage() {
      this.currentIndex = Math.floor(Math.random() * this.messageArray.length);
    },
    annotateImage(event) {
      if (!this.artwork.annotateEnabled) return;

      const { imageWrapper, artwork } = this.$refs;
      const { originalWidth, originalHeight } = artwork;
      const containerRect = imageWrapper.getBoundingClientRect();
      const containerAspectRatio = containerRect.width / containerRect.height;
      const imageAspectRatio = originalWidth / originalHeight;

      let [canvasWidth, canvasHeight] = [0, 0];

      if (imageAspectRatio > containerAspectRatio) {
        canvasWidth = containerRect.width;
        canvasHeight = originalHeight * (canvasWidth / originalWidth);
      } else {
        canvasHeight = containerRect.height;
        canvasWidth = originalWidth * (canvasHeight / originalHeight);
      }

      const xOffset = (containerRect.width - canvasWidth) / 2;
      const yOffset = (containerRect.height - canvasHeight) / 2;

      let { clientX } = event;
      let { clientY } = event;

      if (event.type === "touchstart") {
        const touch = event.touches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      }

      const x = clientX - containerRect.x - xOffset;
      const y = clientY - containerRect.y - yOffset;
      const points = [...this.artwork.annotatedPoints];

      if (x < 0 || x > canvasWidth || y < 0 || y > canvasHeight) return;

      this.annotationXOffset = xOffset;
      this.annotationYOffset = yOffset;

      points.push({ x, y, mode: this.artwork.annotateMode });

      this.updateArtworkValuesById({
        id: this.artwork.id,
        annotatedPoints: points,
        annotatedCanvasWidth: canvasWidth,
        annotatedCanvasHeight: canvasHeight
      });
    }
  }
};
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/pod/settings.scss";

.artwork-image {
  @include dimension-arrows;
  $this: &;
  $dropzone-bg: #fdf2fa;
  $dropzone-highlight: #dd2672;
  $dropzone-hover: #c11574;
  $dropzone-divider: #fcceee;

  &--with-made {
    #{$this}__spinner {
      display: none;
    }

    #{$this}__preview--empty {
      width: 100%;
      border-radius: 0;
      height: 284px;
      background: $pod-gray-modern-300;
    }

    #{$this}__arrow-shaft {
      display: none;
    }

    #{$this}__loading-wrapper {
      border-radius: 8px 8px 0 0;
    }

    #{$this}__preview-image {
      margin: 0;
    }

    #{$this}__preview,
    #{$this}__preview-wrapper {
      padding: 0;
    }
  }

  &__t-shirt {
    height: 380px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $crafterpay-gray-modern-200;
    background-image: url("https://jiffy.imgix.net/gen/t-shirt-upload-dropzone.png?ixlib=rb-0.3.5&w=550&dpr=2");
    background-repeat: no-repeat;
    background-size: 475px auto;
    background-position-x: center;
    background-position-y: 5px;
    position: relative;

    @include with-tablet {
      align-items: flex-end;
    }

    @include with-desktop {
      background-size: 535px auto;
    }

    &--gang-sheet-upload {
      height: 260px;

      @include with-tablet {
        height: 370px;
      }

      @include with-desktop {
        height: 280px;
      }
    }

    &--gang-sheet-upload-with-canva-enabled {
      height: 310px;

      @include with-tablet {
        height: 420px;
      }

      @include with-desktop {
        height: 330px;
      }
    }
  }

  &__dropzone {
    cursor: pointer;
    padding: 16px;
    border: 1px dashed $dropzone-highlight;
    background: $dropzone-bg;
    border-radius: 4px;
    display: flex;
    align-items: center;
    flex-direction: column;
    text-align: center;
    box-shadow: 0 12px 12px -4px rgba(18, 25, 38, 0.08);

    @include with-tablet {
      -webkit-box-shadow: unset;
      box-shadow: unset;
      padding: 24px 16px 32px;
    }

    &:hover {
      box-shadow: 0 0 10px $dropzone-highlight;
    }

    #{$this}__dropzone-button {
      order: unset;
      margin-top: 9px;
      max-width: 308px;
      background: transparent;
      color: $dropzone-highlight;
      border: 1px solid $dropzone-highlight;
      height: 40px;

      &--link {
        border: none;
        height: unset;
        text-decoration: underline;

        &:hover {
          background: transparent;
        }
      }

      &--standard-artwork {
        color: $primary-light;
        border: 1px solid $primary-light;
      }

      @include with-tablet {
        border-radius: 8px;
      }

      @include with-desktop {
        font-size: 13px;
        padding-left: 2px;
        padding-right: 2px;
      }
    }

    #{$this}__link {
      color: $dropzone-highlight;

      &:hover {
        color: $dropzone-hover;
      }

      &--standard-artwork {
        color: $primary-light;

        &:hover {
          color: $primary;
        }
      }
    }

    &--canva-enabled {
      padding: 82px 100px !important;
    }

    &--cloud {
      background: $crafterpay-gray-modern-100;
      border: none;
      border-radius: 4px;
      position: relative;
      padding: 82px;

      @include with-tablet {
        padding: 60px 24px;
      }

      @include with-desktop {
        padding: 110px 24px;
      }

      &:hover {
        box-shadow: none;
      }

      #{$this}__divider {
        @include with-tablet {
          max-width: 180px;
          margin: 4px 0;
        }

        @include with-desktop {
          max-width: 204px;
          margin: 8px 0;
        }

        &::before {
          border-bottom: solid 1px $dropzone-divider;
          opacity: 1;
        }

        &::after {
          color: $crafterpay-gray-modern-600;
          background: $dropzone-bg;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.42;
          margin-left: -74px;
          padding-left: 8px;
          padding-right: 8px;
        }
      }

      #{$this}__info,
      #{$this}__action,
      #{$this}__dropzone-button {
        z-index: 1;
      }

      #{$this}__title {
        margin: 0;
        color: $black-light;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.47;

        @include with-tablet {
          margin: 8px 0 0;
        }

        @include with-desktop {
          margin: 0;
        }
      }

      #{$this}__action-buttons {
        width: 100%;
      }

      #{$this}__action {
        padding: 10px 8px;
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.47;
        max-width: 232px;

        @include with-tablet {
          padding: 10px 16px;
          margin-top: 0;
          max-width: 200px;
        }

        @include with-desktop {
          max-width: 216px;
        }
      }

      #{$this}__dropzone-button--purchase {
        color: $dropzone-highlight;
        text-align: center;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.59;
        border: 1px solid $dropzone-highlight;
        text-decoration: none;
        margin-top: 9px;
        max-width: 232px;

        @include with-tablet {
          max-width: 200px;
        }

        @include with-desktop {
          max-width: 216px;
        }
      }

      #{$this}__sign-in {
        z-index: 1;
        margin: 8px 0 0;
        font-size: 13px;
        text-align: center;
        line-height: 1.07;
        max-width: 180px;

        a {
          color: $dropzone-highlight;
          text-decoration-line: underline;
          font-weight: 700;
        }

        @include with-large-desktop {
          max-width: 100%;
          margin: 12px 0 0;
        }
      }

      .spangle-notice,
      .puff-notice {
        max-width: 220px;
        margin: 24px 0 10px;

        a {
          color: $dropzone-highlight;
          text-decoration: underline;
        }
      }

      .spangle-notice__bulb-icon,
      .puff-notice__bulb-icon {
        background: $dropzone-highlight;
      }
    }

    &--t-shirt {
      width: 229px;
      margin: 0 auto;
      margin-bottom: 30px;
      padding: 26px;
      outline: 8px solid $dropzone-bg;
      align-items: center;
      justify-content: center;
      position: absolute;

      @include with-desktop {
        margin-bottom: 20px;
      }

      #{$this}__divider {
        margin: 8px 0;
      }

      #{$this}__icon {
        width: 35px;
        height: 35px;
      }

      #{$this}__title,
      #{$this}__action-title {
        font-size: 14px;
      }

      #{$this}__action {
        height: 40px;
        width: 190px;
        padding: 9px;
        display: flex;
        align-items: center;
        justify-content: center;

        @include with-tablet {
          padding: 9px 16px;
        }
      }

      #{$this}__dropzone-button {
        width: 175px;
      }
    }

    &--standard-artwork {
      border-color: $primary-light;
      background: $spindle-light;
      outline-color: $spindle-light;

      &:hover {
        box-shadow: 0 0 10px $primary-light;
      }
    }

    &--gang-sheet {
      width: 100%;
      height: 100%;
      margin-bottom: 0;
    }
  }

  &__cloud {
    position: absolute;
    line-height: 0;
    top: 50%;
    transform: translate(0, -50%);
    z-index: 0;
    width: 100%;
    height: 100%;

    @include with-tablet {
      max-height: 260px;
      max-width: 320px;

      &--canva-enabled {
        max-height: 300px;
        max-width: 360px;
      }
    }

    @include with-desktop {
      max-height: 350px;
      max-width: 450px;

      &--canva-enabled {
        max-height: 400px;
        max-width: 500px;
      }
    }
  }

  &__cloud-box {
    fill: $white;
    height: 100%;
    width: 100%;
  }

  &__cloud-border {
    position: absolute;
    height: 100%;
    width: 100%;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    max-width: 88%;
  }

  &__dropzone-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    margin: 0 0 10px;
    padding: 4px 21px;
    border-radius: 8px;
    background: $primary-light;
    color: $white;
    font-weight: 700;
    order: -1;
    width: 100%;

    @include with-tablet {
      height: 40px;
      margin: 20px 0 4px;
      order: unset;
      pointer-events: all;
      border-radius: 4px;
    }

    @include with-desktop {
      max-width: 308px;
    }

    &:focus,
    &:hover {
      background-color: $primary;
    }

    &--purchase {
      color: $deep-blue;
      background: $white;
      border: 1px solid $deep-blue;
      font-size: 12px;
      margin-bottom: 0;

      &:focus,
      &:hover,
      &:visited,
      &:active {
        background: $white;
        text-decoration: none;
      }

      @include with-xl-mobile {
        font-size: 13px;
      }

      @include with-tablet {
        margin-top: 4px;
        font-size: 12px;
      }
    }

    &--canva {
      padding: 0;
    }
  }

  &__image-wrapper {
    display: flex;
    justify-content: center;

    &--loading {
      min-height: 310px;
    }
  }

  &__image {
    min-height: 50px;
    max-height: 364px !important;
    min-width: 50px;
    max-width: v-bind(imagePreviewMaxWidth);

    width: v-bind(imageWidth);
    height: v-bind(imagePreviewHeight);
    pointer-events: none;

    @include with-tablet {
      max-height: 352px !important;
    }

    @include with-desktop {
      max-height: 364px !important;
    }

    @include with-large-desktop {
      max-height: 348px !important;
    }

    &--full-width {
      width: v-bind(pdpImageWidth);
      max-width: 100%;
      max-height: 60vh !important;
      object-fit: contain;
      max-height: 3000px !important;
    }

    &--fit-to-container {
      width: v-bind(pdpImageWidth);

      object-fit: contain;

      &#{&} {
        max-width: calc(100vw - 100px) !important;
        max-height: 80vh !important;
      }
    }
  }

  &__preview {
    position: relative;
    width: auto;
    height: auto;
    min-height: 200px;
    max-height: 440px;
    border: 0;
    border-radius: 8px;
    line-height: normal;
    padding: 5px;
    margin-top: 30px;

    &--with-error {
      #{$this}__arrow-shaft {
        background-color: $red;
      }

      #{$this}__dimension {
        color: $red;
      }

      #{$this}__arrow {
        &::before,
        &::after {
          background-color: $red;
        }
      }
    }

    &--without-width {
      margin-top: 0;
    }

    &--full-height {
      max-height: unset;
      min-height: unset;
      width: fit-content;
      margin: 0 auto;
    }

    &--fit-to-container {
      max-height: unset;
      min-height: unset;
      width: fit-content;
      margin: 0 auto;
    }
  }

  &__preview-wrapper {
    position: relative;
    height: 100%;
    padding: 0 0 5px 36px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &--without-height {
      padding: 0;
    }

    &--max-width {
      padding: 0 0 5px 15px;

      @include with-tablet {
        padding: 0 0 5px 10px;
      }
    }
  }

  &__comparison-slider-label {
    display: none;

    @include with-tablet {
      display: unset;
    }
  }

  &__preview-image {
    max-height: 100%;
    width: 100%;
    position: relative;
    background: v-bind(artworkBackground);
    border-radius: 8px;
    text-align: center;
    margin-bottom: 8px;

    &--transparent-background {
      background: $transparent-bg;
    }

    &--transparent-background#{&}--standard-artwork {
      background: $transparent-bg-2;
    }

    &--loaded {
      width: v-bind(canvasWidthPx);
      height: v-bind(canvasHeightPx);
      text-align: v-bind(imageAlignment);
      border-radius: 0;
    }

    &--full-width {
      width: 100%;
    }
  }

  &__preview-overlay-loader {
    position: absolute;
    background: #000000ba;
    color: $white;
    z-index: 5;
    width: 100%;
    bottom: 0;
    padding: 16px;
    font-weight: bold;
    display: table;

    @include with-desktop {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &--editing-size {
      justify-content: center;
    }

    &--tips-wrapper {
      justify-content: flex-start;
    }

    &--tips-wrapper-modal {
      display: table;
    }
  }

  &__preview-overlay-loader-icon {
    width: 15px;
    height: 15px;
    color: $crafterpay-gray-modern-300;
    margin-right: 10px;
  }

  &__arrow-shaft {
    &--horizontal-compact {
      top: -15px;
    }

    &--vertical-compact {
      left: -20px;
    }

    &--horizontal#{&}--standard-artwork {
      max-width: 100%;
      left: v-bind(arrowShaftHorizontalLeftPadding);
      width: v-bind(arrowShaftHorizontalWidth);
    }

    &--vertical#{&}--standard-artwork {
      max-height: 100%;
      top: v-bind(arrowShaftVerticalTopPadding);
      height: v-bind(arrowShaftVerticalHeight);
    }
  }

  &__preview-overlay-loader-tips-icon {
    width: 20px;
    height: 20px;
    margin-right: 2px;
  }

  &__preview-overlay-loader-jiffy-tips {
    background: #1d90ff;
    margin-right: 5px;
    margin-bottom: 5px;
    padding: 2px 5px 2px 5px;
    border-radius: 5px;
    display: flex;
    justify-content: flex-start;
    align-items: self-end;
    width: 105px;

    @include with-desktop {
      justify-content: center;
      width: auto;
    }

    &--modal {
      width: 125px;
    }
  }

  &__dimension {
    background: $white;
    font-weight: 700;
    padding: 4px;

    &--vertical {
      transform: rotate(-90deg);
    }
  }

  &__spinner {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 80px;
    margin: auto;
  }

  &__link {
    text-decoration: underline;
    font-weight: 700;
  }

  &__info {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__icon {
    width: 18px;
    height: 18px;
    color: $white;

    @include with-tablet {
      color: $dropzone-highlight;
      width: 40px;
      height: 40px;
    }

    &--standard-artwork {
      color: $primary-light;
    }
  }

  &__title {
    margin: 4px 0 0;
    color: $black-light;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.5;
  }

  &__subtitle {
    color: $black-light;
    text-align: center;
    font-weight: 600;
    line-height: 1.5;
  }

  &__divider {
    margin: 12px 0;
    text-align: center;
    width: 100%;
    max-width: 292px;
    position: relative;
    padding: 10px;

    &::before {
      position: absolute;
      content: "";
      top: 50%;
      left: 0;
      right: 0;
      margin-top: -1px;
      border-bottom: solid 1px $primary-light;
      opacity: 0.2;
    }

    &::after {
      position: absolute;
      content: "Or choose a design from";
      top: 50%;
      color: $crafterpay-gray-modern-600;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.42;
      margin-top: -10px;
      padding: 0 6px;
      background: $dropzone-bg;
      margin-left: -72px;
    }

    &--standard-artwork {
      &::after {
        background: $spindle-light;
      }
    }
  }

  &__action {
    cursor: pointer;
    appearance: none;
    border: none;
    padding: 10px 16px;
    border-radius: 8px;
    background: $dropzone-highlight;
    width: 100%;
    max-width: 308px;
    color: $white;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.42;

    &:hover {
      background: $dropzone-hover;
    }

    @include with-tablet {
      font-size: 16px;
      line-height: 1.5;
    }

    &--standard-artwork {
      background: $primary-light;

      &:hover {
        background: $primary;
      }
    }
  }

  &__action-title {
    display: none;

    @include with-tablet {
      display: block;
    }

    &--mobile {
      display: block;
      font-size: 14px;

      @include with-tablet {
        display: none;
      }
    }
  }

  &__action-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    @include with-tablet {
      display: none;
    }
  }

  &__zoom-button {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba($text-black, 0.6);
    border-style: none;
    position: absolute;
    right: 5px;
    bottom: 5px;
    z-index: 5;

    @include with-desktop {
      right: -15px;
      bottom: -15px;
    }

    &--t-shirt {
      right: 0;
      bottom: 0;
      background-color: rgba($borneo-pink-gradient-start, 0.9);
    }
  }

  &__zoom-icon {
    width: 24px;
    height: 24px;
    color: $white;
  }

  &__loader-indicator {
    width: 20px;
  }

  &__loader-text {
    text-align: left;
    display: table-row;

    @include with-desktop {
      display: inline;
    }

    &--modal {
      display: table-row;
    }
  }

  &__sparkle-gif-container {
    position: absolute;
    z-index: 5;
    width: 100%;
    height: 100%;
  }

  &__loading-wrapper {
    position: absolute;
    z-index: 3;
    width: 100%;
    height: 101%; // 1% to compensate blur
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    background: rgba(236, 236, 236, 0.8);
    backdrop-filter: blur(22px);
  }

  &__loading-box-content {
    width: 80%;
    max-width: 300px;
    max-height: 80%;
    padding: 10px 18px;
    border-radius: 8px;
    background: rgba($white, 0.83);
    backdrop-filter: blur(7px);
  }

  &__loading-box-content-icon {
    width: 40px;
    height: 40px;
    color: $crafterpay-gray-modern-400;
    fill: transparent;
  }

  &__sparkle-gif {
    width: 100%;
    height: 100%;
    opacity: 0.7;
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba($black, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  &__loader-spinner {
    width: 20px;
    height: 20px;
  }

  &__cancel {
    margin: 0 5px;
    margin-left: auto;
    color: $white;
    text-decoration: underline;
    background: none;
    border: none;

    &--dtf {
      font-size: 14px;
      font-weight: 700;
      color: $primary-light;
      display: flex;
      align-items: center;
      margin: 0 auto;
      text-decoration: none;
    }

    &--dtf-v2 {
      margin: -10px auto 0 auto;
    }
  }

  &__cancel-icon {
    width: 14px;
    height: 14px;
    margin-right: 3px;
  }

  &__image-ready-block {
    font-family: $font-family-secondary;
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 8px 32px;

    @include with-desktop {
      font-size: 18px;
      line-height: 20px;
      padding: 8px 0 16px 0;
      justify-content: center;
    }

    &--floating {
      position: absolute;
      bottom: 0;
      padding: 10px 8px;
      color: $white;
      background-color: rgba($black, 0.75);
      width: 100%;
      gap: 7px;
      align-items: flex-start;

      @include with-desktop {
        align-items: center;
        padding: 12px 0 16px 0;
      }
    }
  }

  &__image-ready-text-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__image-ready-subtext {
    font-size: 13px;
    line-height: 18px;
    font-weight: 400;
    margin-top: 3px;

    @include with-desktop {
      font-size: 14px;
      line-height: 14px;
    }
  }

  &__image-ready-tick {
    height: 32px;
    width: 32px;
    flex-shrink: 0;
  }

  &__keep {
    background-color: rgba(0, 255, 0, 0.5);
    width: 10px;
    height: 10px;
    border-radius: 3px;
    position: absolute;
  }

  &__remove {
    background-color: rgba(255, 0, 0, 0.5);
    width: 10px;
    height: 10px;
    border-radius: 5px;
    position: absolute;
  }

  &__action-buttons {
    display: flex;
    flex-direction: column-reverse;
    gap: 20px;
    width: 100%;
    align-items: center;
    justify-content: center;

    @include with-desktop {
      flex-direction: row;
    }
  }

  &__gang-sheet-note {
    display: flex;
    align-items: center;
    text-align: left;
    gap: 8px;
    font-size: 13px;
    margin-bottom: 16px;
  }

  &__gang-sheet-note-icon {
    width: 38px;
    height: 24px;
    background: $bulb-background;
    border-radius: 12px;
    padding: 2px;

    @include with-desktop {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
